// HELPDOCS1 (docs/build-briefs/HELPDOCS1-guides-and-screen-help.md): the one way a parsed guide (ownerHelpModel.ts parseGuide)
// is drawn, shared by the Help panel of the location and franchisor consoles (helpPanel.ts) and the Snip & Sip Admin Help
// section (softwareOwnerHelp.ts). Every shown word is set with textContent and element creation, never as markup, so a guide
// cannot inject anything. A link is drawn only for a span the parser accepted (https://snipsip.com/help/ pages), and opens in a
// new tab. Images are fetched with the person's session token and shown from a blob URL, because an <img src> cannot carry the
// header; the caller revokes the URLs it is handed.

import { HELP_IMAGE_FAILED, type HelpBlock, type Span } from "./ownerHelpModel.ts";

/** The id a section heading carries in the page, so a screen can scroll to it. */
export function sectionDomId(sectionId: string): string {
  return `help-sec-${sectionId}`;
}

export interface BlockRenderHost {
  /** An image GET with the session's token, as a blob; null on any failure. */
  loadImage(name: string): Promise<Blob | null>;
  /** False once the person moved on, so a late reply is dropped instead of drawn over the wrong guide. */
  isCurrent(): boolean;
  /** Hands over a blob URL the caller must revoke when the guide closes. */
  keepUrl(url: string): void;
  /** Opens another guide section (a `guide:<slug>#<section>` link). Absent where guides do not link to each other. */
  openGuide?(slug: string, section: string | undefined): void;
}

export function appendSpans(parent: HTMLElement, spans: Span[], openGuide?: BlockRenderHost["openGuide"]): void {
  for (const span of spans) {
    if (span.href !== undefined && span.href.startsWith("guide:")) {
      const [slug = "", section] = span.href.slice("guide:".length).split("#");
      if (openGuide === undefined) {
        parent.append(span.text);
        continue;
      }
      const link = document.createElement("a");
      link.href = "#";
      link.textContent = span.text;
      link.dataset["guide"] = slug;
      link.addEventListener("click", (event) => {
        event.preventDefault();
        openGuide(slug, section);
      });
      parent.append(link);
    } else if (span.href !== undefined) {
      const link = document.createElement("a");
      link.href = span.href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = span.text;
      parent.append(link);
    } else if (span.bold) {
      const strong = document.createElement("strong");
      strong.textContent = span.text;
      parent.append(strong);
    } else {
      parent.append(span.text);
    }
  }
}

/** Draws every block into `article` and resolves when every picture has been tried. */
export async function renderGuideBlocks(
  article: HTMLElement,
  blocks: HelpBlock[],
  host: BlockRenderHost,
): Promise<void> {
  const pending: Promise<void>[] = [];
  for (const block of blocks) {
    if (block.kind === "heading") {
      const heading = document.createElement(block.level === 2 ? "h3" : "h4");
      heading.className = "so-help-heading";
      if (block.id !== undefined) {
        heading.id = sectionDomId(block.id);
        heading.tabIndex = -1;
      }
      appendSpans(heading, block.spans, host.openGuide);
      article.append(heading);
    } else if (block.kind === "paragraph") {
      const p = document.createElement("p");
      appendSpans(p, block.spans, host.openGuide);
      article.append(p);
    } else if (block.kind === "list") {
      const list = document.createElement(block.ordered === true ? "ol" : "ul");
      if (block.ordered === true && block.start !== undefined && block.start !== 1) {
        (list as HTMLOListElement).start = block.start;
      }
      for (const itemSpans of block.items) {
        const li = document.createElement("li");
        appendSpans(li, itemSpans, host.openGuide);
        list.append(li);
      }
      article.append(list);
    } else if (block.kind === "table") {
      const table = document.createElement("table");
      table.className = "so-help-table";
      const thead = document.createElement("thead");
      const headRow = document.createElement("tr");
      for (const cell of block.head) {
        const th = document.createElement("th");
        th.scope = "col";
        appendSpans(th, cell, host.openGuide);
        headRow.append(th);
      }
      thead.append(headRow);
      const tbody = document.createElement("tbody");
      for (const row of block.rows) {
        const tr = document.createElement("tr");
        for (const cell of row) {
          const td = document.createElement("td");
          appendSpans(td, cell, host.openGuide);
          tr.append(td);
        }
        tbody.append(tr);
      }
      table.append(thead, tbody);
      article.append(table);
    } else {
      const figure = document.createElement("figure");
      figure.className = "so-help-figure";
      const holder = document.createElement("div");
      holder.className = "so-help-figure-holder";
      holder.textContent = "Loading the picture...";
      figure.append(holder);
      if (block.caption !== "") {
        const caption = document.createElement("figcaption");
        caption.textContent = block.caption;
        figure.append(caption);
      }
      article.append(figure);
      pending.push(
        host.loadImage(block.name).then((blob) => {
          if (!host.isCurrent()) return;
          if (blob === null) {
            holder.textContent = HELP_IMAGE_FAILED;
            return;
          }
          const url = URL.createObjectURL(blob);
          host.keepUrl(url);
          const img = document.createElement("img");
          img.src = url;
          img.alt = block.alt;
          img.className = "so-help-image";
          // A tap on the picture opens it full size in a new tab, so small text in a cropped screen can be read.
          const open = document.createElement("a");
          open.href = url;
          open.target = "_blank";
          open.rel = "noopener noreferrer";
          open.append(img);
          holder.replaceChildren(open);
        }),
      );
    }
  }
  await Promise.all(pending);
}
